import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { storyService } from '../services/storyService';
import { useAuth } from '../context/AuthContext';

const StoryCreator = () => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      setError('Please enter a title for your story');
      return;
    }

    if (!user) {
      setError('You must be logged in to save a story');
      navigate('/login');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const storyData = {
        title,
        description
      };

      console.log('Saving story:', storyData);
      
      const result = await storyService.createStory(storyData);
      
      console.log('Story saved successfully:', result);
      navigate('/');
    } catch (err) {
      console.error('Error saving story:', err);
      setError('Failed to save story. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onBack = () => {
    navigate('/');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="story-creator bg-white rounded-3xl shadow-dreamy p-6"
    >
      <div className="flex justify-between items-center mb-6">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-4 py-2 rounded-xl font-medium flex items-center gap-2"
          onClick={onBack}
        >
          <SafeIcon icon={FiIcons.FiArrowLeft} className="text-lg" />
          Back
        </motion.button>
        <h2 className="text-2xl font-display font-bold text-purple-700">
          Create New Story
        </h2>
        <div className="w-[100px]"></div> {/* Spacer for alignment */}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="title" className="block text-purple-700 font-medium mb-2">
            Story Title
          </label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-2 rounded-xl border border-purple-200 focus:border-purple-400 focus:ring focus:ring-purple-300 focus:ring-opacity-50"
            placeholder="Enter a captivating title..."
            disabled={isSubmitting}
          />
        </div>

        <div className="mb-6">
          <label htmlFor="description" className="block text-purple-700 font-medium mb-2">
            Description
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-4 py-2 rounded-xl border border-purple-200 focus:border-purple-400 focus:ring focus:ring-purple-300 focus:ring-opacity-50"
            placeholder="What is your story about?"
            rows="4"
            disabled={isSubmitting}
          />
        </div>

        <div className="flex justify-end">
          <motion.button
            type="submit"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <SafeIcon icon={FiIcons.FiSave} className="text-lg" />
                Save Story
              </>
            )}
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
};

export default StoryCreator;